import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { DragIndicator, Close } from '@mui/icons-material';
import Draggable from 'react-draggable';
import ResizeHandle from './ResizeHandle';

interface Position {
  x: number;
  y: number;
}

interface Size {
  width: number;
  height: number;
}

interface DraggableDialogProps {
  isOpen: boolean;
  title: string;
  position: Position;
  dialogSize: Size;
  onClose: () => void;
  onDrag?: (e: any, data: { x: number; y: number }) => void;
  onResizeStart?: (e: React.MouseEvent) => void;
  children: React.ReactNode;
}

const DraggableDialog = ({
  isOpen,
  title,
  position,
  dialogSize,
  onClose,
  onDrag,
  onResizeStart,
  children,
}: DraggableDialogProps) => {
  const nodeRef = React.useRef<HTMLDivElement>(null);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        zIndex: 1300,
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
      }}
    >
      <Draggable
        nodeRef={nodeRef}
        defaultPosition={position}
        onDrag={onDrag}
        handle=".dialog-drag-handle"
        bounds="parent"
      >
        <Paper
          ref={nodeRef}
          elevation={24}
          style={{
            width: dialogSize.width,
            height: dialogSize.height,
            position: 'relative',
            maxWidth: 'none',
            maxHeight: 'none',
            display: 'flex',
            flexDirection: 'column',
            pointerEvents: 'auto',
          }}
        >
          <Box
            className="dialog-drag-handle"
            sx={{
              cursor: 'move',
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DragIndicator color="action" />
              <Typography variant="h6">{title}</Typography>
            </Box>
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </Box>
          <Box
            sx={{
              p: 2,
              flex: 1,
              overflow: 'auto',
              minHeight: 0,
              bgcolor: 'background.paper',
            }}
          >
            {children}
          </Box>
          <ResizeHandle onMouseDown={(e) => onResizeStart?.(e)} />
        </Paper>
      </Draggable>
    </div>
  );
};

export default DraggableDialog;
